import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Tour, TOUR_NAMES, TourProps, TourStartCard } from './Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { TourTooltip } from './TourTooltip/TourTooltip'
import { useUserCompletedProductTours } from 'hooks/queries/users/useUserCompletedProductTours'
import { useUpdateUserCompletedProductTour } from 'hooks/queries/users/useUpdateUserCompletedProductTour'
import { Mask, MaskOptions, Step, Walktour, WalktourLogic } from 'walktour'
import React, { useEffect, useRef, useState } from 'react'
import { isElementInViewport } from 'utils/isElementInViewport'
import { StartTourTooltip } from './StartTourTooltip/StartTourTooltip'
import { createPortal } from 'react-dom'
import useOutsideClick from 'hooks/useOutsideClick'
import { useModals } from 'almanac-ui/Modal'
import { CompletionModal } from './CompletionModal/CompletionModal'
import { useProductTourObserver } from './hooks/useProductTourObserver'
import { useAuth } from 'hooks/UseAuth'

type Props = TourProps & {
  steps: Step[]
  tour: Tour
  startCard: TourStartCard
  zIndex?: number
}

const CustomTooltip = (
  props: WalktourLogic & {
    zIndex?: number
    tour: Tour
  },
) => {
  const { stepIndex, tour, allSteps, close, next, prev, stepContent, zIndex } = props
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [el, setEl] = useState<HTMLElement | null>(null)
  const step = allSteps[stepIndex]
  const { description, title } = stepContent

  useOutsideClick(tooltipRef, () => {
    close()
  })

  useEffect(() => {
    const stepEl = document.querySelector(step.selector)
    if (stepEl) {
      setEl(stepEl as HTMLElement)

      if (!isElementInViewport(stepEl)) {
        stepEl?.scrollIntoView({
          behavior: 'instant',
        })
      }
    }
  }, [step.selector])

  const getSteps = () => {
    return {
      currentStep: stepIndex + 1,
      totalSteps: allSteps.length,
      onBack: () => prev(),
      onNext: () => next(),
    }
  }

  if (!el) {
    return <></>
  }

  return (
    <>
      <TourTooltip
        ref={tooltipRef}
        tour={tour}
        title={title}
        content={description}
        steps={getSteps()}
        close={() => close()}
        zIndex={zIndex}
      />
    </>
  )
}

const FeatureFlaggedProductTour = ({ steps, tour, zIndex, startCard, beforeStart }: Props) => {
  const { openModal, stack: modalStack } = useModals()
  const { data: flags, isLoading } = useUserCompletedProductTours()
  const { mutate: updateProductTour } = useUpdateUserCompletedProductTour()
  const { availableTour, registerProductTour, unregisterProductTour } = useProductTourObserver()
  const isModalOpen = modalStack.length > 0
  const auth = useAuth()

  const [stepCheckComplete, setStepCheckComplete] = useState(false)
  const [activated, setActivated] = useState(false)
  const numSteps = steps.length

  const completed = flags?.[tour] === true
  const persistentStepsCompleted = typeof flags?.[tour] === 'number' ? flags[tour] : 0
  const stepsCompleted = completed ? numSteps : (persistentStepsCompleted as number)

  const [step, setStep] = useState(stepsCompleted)

  useEffect(() => {
    setStep(stepsCompleted)
  }, [stepsCompleted])

  useEffect(() => {
    if (completed && !isLoading && stepCheckComplete && auth?.onboarded) {
      registerProductTour(tour)
      if (availableTour === tour) {
        setActivated(false)
      }
    } else {
      unregisterProductTour(tour)
    }

    return () => {
      unregisterProductTour(tour)
    }
  }, [tour, stepCheckComplete, completed, isLoading, availableTour, auth?.onboarded]) // eslint-disable-line react-hooks/exhaustive-deps

  const activate = async () => {
    if (beforeStart) {
      await beforeStart()
    }
    setActivated(true)
  }

  const completeTour = () => {
    updateProductTour({ tour, step: true })
  }

  const updateLastStepCompleted = (step: number) => {
    setStep(step)

    if (step >= numSteps) {
      completeTour()
      openModal(CompletionModal)
    } else {
      updateProductTour({ tour, step: Math.max(0, step) })
    }
  }

  // Make sure all elements in steps exist before starting the tour
  useEffect(() => {
    if (!stepCheckComplete) {
      let interval: ReturnType<typeof setInterval> | null

      const checkElementsExist = () => {
        const allElementsExist = steps.every((step) => !!document.querySelector(step.selector))

        if (allElementsExist) {
          setStepCheckComplete(true)

          if (interval !== null) {
            clearInterval(interval)
          }
        }
      }

      checkElementsExist()
      interval = setInterval(checkElementsExist, 1000)

      return () => {
        if (interval) {
          clearInterval(interval)
        }
      }
    }
  }, [stepCheckComplete, setStepCheckComplete, steps])

  if (completed || isLoading || !stepCheckComplete || isModalOpen) {
    return <></>
  }

  if (!activated) {
    return createPortal(
      <StartTourTooltip
        {...startCard}
        tour={tour}
        tourName={TOUR_NAMES[tour]}
        currentStep={step}
        totalSteps={steps.length}
        handleClose={completeTour}
        handleCta={() => activate()}
      />,
      document.body,
    )
  }

  const CustomTooltipWrapper = (props: WalktourLogic | undefined) => {
    if (!props) {
      return <></>
    }

    return (
      <CustomTooltip
        {...props}
        tour={tour}
        zIndex={zIndex}
        close={() => setActivated(false)}
        prev={() => {
          updateLastStepCompleted(props.stepIndex - 1)
          props.prev()
        }}
        next={() => {
          updateLastStepCompleted(props.stepIndex + 1)
          props.next()
        }}
      />
    )
  }

  const CustomMask = (maskOptions: MaskOptions) => {
    if (!activated) {
      return <></>
    }

    return (
      <Mask
        {...maskOptions}
        close={() => {
          setActivated(false)
        }}
      />
    )
  }

  return (
    <Walktour
      isOpen
      steps={steps}
      customTooltipRenderer={CustomTooltipWrapper}
      renderMask={CustomMask}
      initialStepIndex={step}
      rootSelector="body"
      maskRadius={6}
      maskPadding={-1}
      zIndex={9000}
    />
  )
}

export const ProductTour = (props: Props) => {
  const { stepFilter, eligible = true, steps } = props
  const tourEnabled = useFlipperFlag('product_tours_2025')
  const auth = useAuth()

  const { isMobile } = useResponsiveDesign()

  if (!tourEnabled || isMobile || !eligible || !auth?.onboarded) {
    return <></>
  }

  return <FeatureFlaggedProductTour key={props.tour} {...props} steps={stepFilter?.(steps) ?? steps} />
}
